import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useListService from '@/data/hook/useListService';
import { questionExamGradeQueries } from '@/data/query-keys/questionExamGradeQueryKey';
import {
  createQuestionExamGrade,
  updateQuestionExamGrade,
} from '@/data/services/questionExamGradeServices';
import Activity from '@/models/Activity';
import { Question } from '@/models/Question';
import QuestionExamGrade from '@/models/QuestionExamGrade';
import normalize from '@/utils/statistics/normalize';

type UseQuestionExamGradesProps = {
  examAreaId: number;
};

type SaveGradeArgs = {
  question: Question;
  maxGrade: number;
};

export type QuestionExamGradeContextValue = {
  grades: QuestionExamGrade[];
  cacheManager: ListCacheManager<QuestionExamGrade>;
  isLoading: boolean;
  getGradeForQuestion: (question: Question) => QuestionExamGrade | undefined;
  getActivityTotal: (activity: Activity) => number;
  saveGrade: (args: SaveGradeArgs) => Promise<QuestionExamGrade>;
  isMutating: boolean;
};

const gradeKey = (questionType: string, questionId: number) =>
  `${questionType}:${questionId}`;

const useQuestionExamGrades = ({
  examAreaId,
}: UseQuestionExamGradesProps): QuestionExamGradeContextValue => {
  const {
    results: grades,
    isInitialLoading,
    cacheManager,
  } = useListService<QuestionExamGrade>({
    ...questionExamGradeQueries.list({
      examAreaId,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: !!examAreaId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const gradesIndex = useMemo(() => {
    const map = new Map<string, QuestionExamGrade>();
    for (const grade of grades) {
      map.set(gradeKey(grade.questionType, grade.questionId), grade);
    }
    return map;
  }, [grades]);

  const getGradeForQuestion = useCallback(
    (question: Question): QuestionExamGrade | undefined => {
      if (!question.id) return undefined;
      return gradesIndex.get(gradeKey(question.type, question.id));
    },
    [gradesIndex],
  );

  const getActivityTotal = useCallback(
    (activity: Activity): number => {
      const questions = activity.questions ?? [];
      const sum = questions.reduce((sum, question) => {
        const grade = getGradeForQuestion(question);
        return sum + (grade?.maxGrade ?? 0);
      }, 0);
      return normalize(sum, 2);
    },
    [getGradeForQuestion],
  );

  const { mutateAsync: createGrade, isPending: isCreating } = useMutation({
    mutationFn: ({ question, maxGrade }: SaveGradeArgs) =>
      createQuestionExamGrade(examAreaId, {
        questionType: question.type,
        questionId: question.id,
        maxGrade,
      }),
    onSuccess: grade => cacheManager.add(grade),
  });

  const { mutateAsync: updateGrade, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, maxGrade }: { id: number; maxGrade: number }) =>
      updateQuestionExamGrade(examAreaId, id, { maxGrade }),
    onSuccess: grade => cacheManager.update(grade),
  });

  const saveGrade = async ({
    question,
    maxGrade,
  }: SaveGradeArgs): Promise<QuestionExamGrade> => {
    const existing = getGradeForQuestion(question);
    if (!existing) return createGrade({ question, maxGrade });
    if (existing.maxGrade === maxGrade) return existing;
    return updateGrade({ id: existing.id, maxGrade });
  };

  return {
    grades,
    cacheManager,
    isLoading: isInitialLoading,
    getGradeForQuestion,
    getActivityTotal,
    saveGrade,
    isMutating: isCreating || isUpdating,
  };
};

export default useQuestionExamGrades;
